import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  AgentTaskTimingCreateInput,
  AgentTaskTimingData,
} from '@sb/feathers-types';
import { getApp } from '@sbrc/services/feathers-client/utils';

import type { MessageTiming } from './MessageFeedback';
import { estimateConversationMinutes } from './taskTiming';
import type { ChatMessage } from './types';
import {
  useSessionTaskTimings,
  type SessionTaskTimingOperations,
} from './useSessionTaskTimings';

/**
 * When the operator last submitted a timing in this session, or null before
 * the first. The row's own timestamp, so the minutes they spent deciding on
 * that log belong to the next window rather than to nobody.
 */
function latestSubmissionAt(
  timings: ReadonlyMap<string, AgentTaskTimingData>,
): string | null {
  let latest: string | null = null;

  for (const row of timings.values()) {
    if (latest === null || Date.parse(row.createdAt) > Date.parse(latest)) {
      latest = row.createdAt;
    }
  }

  return latest;
}

/** Owns timing history, draft ownership, and per-reply timing actions. */
export function useMessageTaskTimings(
  currentSessionID: string | null,
  messages: ChatMessage[],
  enabled: boolean,
) {
  const feathersApp = useMemo(() => getApp(), []);

  // Each reply's clock logs one agentTaskTimings row against that message;
  // agent-bot forwards it to the message's turn span so the triage report
  // can attribute time saved to the step that finished the task.
  const taskTimingOperations = useMemo<SessionTaskTimingOperations>(
    () => ({
      list: async (sessionID) => {
        const fetchPage = async (
          skip: number,
        ): Promise<AgentTaskTimingData[]> => {
          const result = await feathersApp.service('agentTaskTimings').find({
            query: {
              sessionID,
              $limit: 100,
              $skip: skip,
              // Oldest-first keeps concurrent appends from shifting later
              // offsets; id makes equal timestamps deterministic.
              $sort: { createdAt: 1, id: 1 },
            },
          });

          if (Array.isArray(result)) return result;
          if (!('data' in result)) return [];

          if (
            skip + result.data.length >= result.total ||
            result.data.length === 0
          ) {
            return result.data;
          }

          return [
            ...result.data,
            ...(await fetchPage(skip + result.data.length)),
          ];
        };

        return fetchPage(0);
      },
      create: async (
        sessionID,
        messageID,
        minutesByHand,
        minutesWithAgent,
        taskName,
      ) => {
        const input: AgentTaskTimingCreateInput = {
          sessionID,
          messageID,
          taskName,
          minutesByHand,
          minutesWithAgent,
        };

        return (await feathersApp
          .service('agentTaskTimings')
          .create(input)) as AgentTaskTimingData;
      },
    }),
    [feathersApp],
  );

  const {
    timingByMessageID,
    log: logTaskTiming,
    isLoaded,
  } = useSessionTaskTimings({
    sessionID: enabled ? currentSessionID : null,
    operations: taskTimingOperations,
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.warn(
        'RoutineAgentChatMount: failed to load agentTaskTimings',
        error,
      );
    },
  });

  // Only the newest reply can take an estimate, so one exchange yields at
  // most one row and a later estimate lands on the step that earned it,
  // not on an earlier message the operator scrolled back to.
  const latestAssistantMessageID = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];

      if (message.role === 'assistant' && message.messageID !== undefined) {
        return message.messageID;
      }
    }

    return null;
  }, [messages]);

  // Opening a timing card claims that reply until the operator logs or
  // cancels it. A later reply must not discard an in-progress estimate.
  const [timingDraftMessageID, setTimingDraftMessageID] = useState<
    string | null
  >(null);

  useEffect(() => {
    setTimingDraftMessageID(null);
  }, [currentSessionID, enabled]);

  // Where a suggested duration is measured FROM. Only the start is resolved
  // ahead of time: the end is read when the card opens, so the time the
  // operator spends reading the reply is inside the window rather than
  // outside it.
  const [anchor, setAnchor] = useState<{
    sessionID: string;
    messageID: string;
    startedAt: string;
  } | null>(null);

  // The duration the card shows, measured at the moment it opened.
  const [openEstimate, setOpenEstimate] = useState<{
    messageID: string;
    minutes: number;
  } | null>(null);

  useEffect(() => {
    setOpenEstimate(null);
  }, [currentSessionID, enabled]);

  const estimateMessageID = timingDraftMessageID ?? latestAssistantMessageID;

  useEffect(() => {
    if (
      !enabled ||
      !isLoaded ||
      currentSessionID === null ||
      estimateMessageID === null ||
      timingByMessageID.has(estimateMessageID)
    )
      return undefined;
    let canceled = false;
    const messageID = estimateMessageID;
    const sessionID = currentSessionID;

    const load = async () => {
      // A later log measures from the previous submission, so two logs in
      // one session can never claim the same minutes.
      const submitted = latestSubmissionAt(timingByMessageID);

      if (submitted !== null) return submitted;

      // The first log measures from the message that opened the session.
      const result = await feathersApp.service('agentMessages').find({
        query: {
          sessionID,
          role: 'user',
          $limit: 1,
          $sort: { sequence: 1 },
        },
      });

      if (Array.isArray(result)) return result[0]?.createdAt ?? null;
      if ('data' in result) return result.data[0]?.createdAt ?? null;

      return null;
    };

    // Resolve the anchor as soon as a reply is available, so opening the
    // card only has to read the clock.
    load()
      .then((startedAt) => {
        if (!canceled && startedAt != null)
          setAnchor({ sessionID, messageID, startedAt });
      })
      .catch(() => {
        // Keep the editable 10-minute default if no anchor is available.
      });

    return () => {
      canceled = true;
    };
  }, [
    enabled,
    isLoaded,
    currentSessionID,
    estimateMessageID,
    feathersApp,
    timingByMessageID,
  ]);

  const timingForMessage = useCallback(
    (messageID: string): MessageTiming | undefined => {
      if (!enabled) return undefined;

      const recorded = timingByMessageID.get(messageID) ?? null;

      // The newest reply can start an estimate; an already-open draft keeps
      // that permission until the operator logs or cancels it.
      const canLog =
        recorded === null &&
        (timingDraftMessageID === null
          ? messageID === latestAssistantMessageID
          : messageID === timingDraftMessageID);

      if (!canLog && recorded === null) return undefined;

      const warnAndRethrow = (what: string) => (err: unknown) => {
        // eslint-disable-next-line no-console
        console.warn(`RoutineAgentChatMount: failed to ${what}`, err);

        throw err;
      };

      return {
        canLog,
        suggestedMinutes:
          openEstimate?.messageID === messageID
            ? openEstimate.minutes
            : undefined,
        recorded: recorded && {
          minutesByHand: recorded.minutesByHand,
          minutesWithAgent: recorded.minutesWithAgent,
          taskName: recorded.taskName,
        },
        onLog: (minutesByHand, minutesWithAgent, taskName) =>
          logTaskTiming(messageID, minutesByHand, minutesWithAgent, taskName)
            .then(() =>
              setTimingDraftMessageID((current) =>
                current === messageID ? null : current,
              ),
            )
            .catch(warnAndRethrow('persist agentTaskTiming')),
        onOpenChange: (nextIsOpen) => {
          // Close the window against the clock, not against the reply's own
          // timestamp — the operator reads the reply before logging it.
          if (nextIsOpen) {
            const startedAt =
              anchor !== null &&
              anchor.sessionID === currentSessionID &&
              anchor.messageID === messageID
                ? anchor.startedAt
                : null;

            const minutes =
              startedAt === null
                ? null
                : estimateConversationMinutes(
                    startedAt,
                    new Date().toISOString(),
                  );

            setOpenEstimate(minutes === null ? null : { messageID, minutes });
          }

          setTimingDraftMessageID((current) => {
            if (nextIsOpen) return messageID;

            return current === messageID ? null : current;
          });
        },
      };
    },
    [
      enabled,
      anchor,
      openEstimate,
      currentSessionID,
      timingByMessageID,
      latestAssistantMessageID,
      timingDraftMessageID,
      logTaskTiming,
    ],
  );

  return timingForMessage;
}
